import { Center, Flex, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { useRef } from 'react';

import Modal from '../../../../../../../components/Modal';
import Button from '../../../../../../../components/ui/buttons/Button';
import { useFilePicker } from '../../../../../../../hooks';
import { pluralize } from '../../../../../../../utils/helpers';
import { PHOTO_INPUT_ACCEPT } from '../../../photos/constants';
import ShotTile from '../ShotTile';
import TaskSuccessState from './TaskSuccessState';
import { TodoList } from './useTodoList';

type CarPhotosModalProps = {
  isOpen: boolean;
  onClose: () => void;
  todoList: TodoList;
};

const CarPhotosModal = ({ isOpen, onClose, todoList }: CarPhotosModalProps) => {
  const { vehicleShots, vehicleProgress, uploadShot, isShotProcessing, photoThumbnailUrl } =
    todoList;
  const shots = vehicleShots ?? [];

  const pendingShotTypeRef = useRef<string | null>(null);
  const { fileInputRef, openPicker, handleFileSelected } = useFilePicker((file) => {
    if (pendingShotTypeRef.current) uploadShot(pendingShotTypeRef.current, file);
  });

  const handleShotTap = (photoType: string) => {
    pendingShotTypeRef.current = photoType;
    openPicker();
  };

  const { complete, remaining, total } = vehicleProgress;
  const isUploading = shots.some((shot) => isShotProcessing(shot.photoType));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Photos of your car"
      titleFontSize="22px"
      titleFontWeight={700}
      closeOnOverlayClick={!isUploading}
    >
      {complete ? (
        <TaskSuccessState title={`All ${total} shots in your photo set`} onClose={onClose} />
      ) : (
        <VStack spacing={5} pb={4} align="stretch">
          <input
            ref={fileInputRef}
            type="file"
            accept={PHOTO_INPUT_ACCEPT}
            hidden
            onChange={handleFileSelected}
          />
          <Flex align="center" justify="space-between">
            <Text fontSize="14px" color="charcoal">
              Tap each shot to capture it. Photos save as you go.
            </Text>
            <Text fontSize="13px" fontWeight={700} color="grayMedium" flex="none">
              {remaining} {pluralize('shot', remaining)} to go
            </Text>
          </Flex>
          <SimpleGrid columns={2} spacing="10px">
            {shots.map((shot) => (
              <ShotTile
                key={shot.photoType}
                label={shot.label}
                hint={shot.hint}
                previewUrl={photoThumbnailUrl(shot.photoType)}
                isUploading={isShotProcessing(shot.photoType)}
                disabled={isUploading}
                onTap={() => handleShotTap(shot.photoType)}
              />
            ))}
          </SimpleGrid>
          <Center>
            <Button variant="link" onClick={onClose} isDisabled={isUploading}>
              Finish later
            </Button>
          </Center>
        </VStack>
      )}
    </Modal>
  );
};

export default CarPhotosModal;
